import { useCallback, useEffect, useRef } from 'react';

interface UseTablePaginationParams {
  paginationMode: 'pagination' | 'clientSide' | 'loadMore' | 'infiniteScroll' | 'none';
  pageSize: number;
  stateActions: {
    setPage: (page: number) => void;
    setPageSize: (size: number) => void;
  };
  setTableInfo?: (info: Record<string, unknown>) => void;
  tableInfo?: Record<string, unknown>;
  onLoadMore?: () => void;
  loadMoreStatus?: 'idle' | 'loading' | 'done' | 'success';
  externalDataProvided: boolean;
  remoteHasMore: boolean;
  remoteLoadMore?: () => void;
  remoteIsLoadingMore: boolean;
  scrollContainerRef: React.RefObject<HTMLDivElement | null>;
}

interface UseTablePaginationReturn {
  handlePageChange: (pageIndex: number) => void;
  handlePageSizeChange: (pageSize: number) => void;
}

export function useTablePagination({
  paginationMode,
  pageSize,
  stateActions,
  setTableInfo,
  tableInfo,
  onLoadMore,
  loadMoreStatus,
  externalDataProvided,
  remoteHasMore,
  remoteLoadMore,
  remoteIsLoadingMore,
  scrollContainerRef,
}: UseTablePaginationParams): UseTablePaginationReturn {
  const handlePageChange = useCallback(
    (pageIndex: number) => {
      stateActions.setPage(pageIndex);
      if (setTableInfo && tableInfo) {
        setTableInfo({
          ...tableInfo,
          skipCount: pageIndex * pageSize,
        });
      }
    },
    [stateActions, setTableInfo, tableInfo, pageSize]
  );

  const handlePageSizeChange = useCallback(
    (newPageSize: number) => {
      stateActions.setPageSize(newPageSize);
      if (setTableInfo && tableInfo) {
        setTableInfo({ ...tableInfo, skipCount: 0, maxResultCount: newPageSize });
      }
    },
    [stateActions, setTableInfo, tableInfo]
  );

  // Infinite scroll listener
  useEffect(() => {
    if (paginationMode !== 'infiniteScroll') return;

    const effectiveOnLoadMore =
      onLoadMore ??
      (!externalDataProvided && remoteHasMore ? remoteLoadMore : undefined);
    const isCurrentlyLoadingMore = loadMoreStatus === 'loading' || remoteIsLoadingMore;

    if (!effectiveOnLoadMore) return;

    const container = scrollContainerRef.current;
    if (!container) return;

    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = container;
      if (scrollHeight - scrollTop - clientHeight < 100 && !isCurrentlyLoadingMore) {
        effectiveOnLoadMore();
      }
    };

    container.addEventListener('scroll', handleScroll, { passive: true });
    return () => container.removeEventListener('scroll', handleScroll);
  }, [
    paginationMode,
    onLoadMore,
    loadMoreStatus,
    externalDataProvided,
    remoteHasMore,
    remoteLoadMore,
    remoteIsLoadingMore,
    scrollContainerRef,
  ]);

  return {
    handlePageChange,
    handlePageSizeChange,
  };
}
